import React from 'react';
import { Button } from "@/components/ui/button";
import { PhoneCall, Clock, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = "919840193160";
    const message = "Hello, I would like to book an appointment.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleFaqClick = () => {
    if (location.pathname === '/') {
      // If we're on the home page, scroll to FAQ section
      const faqSection = document.getElementById('faq');
      if (faqSection) {
        faqSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to home and then scroll to FAQ
      navigate('/');
      // Use setTimeout to ensure the navigation completes before scrolling
      setTimeout(() => {
        const faqSection = document.getElementById('faq');
        if (faqSection) {
          faqSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top bar with contact info */}
      <div className="bg-swastik-blue text-white py-2">
        <div className="container mx-auto flex justify-between items-center px-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-sm">
              <PhoneCall className="h-4 w-4 mr-2" />
              <span>Call Us: +91-98401 93160, +91-6385044414</span>
            </div>
            <div className="hidden md:flex items-center text-sm">
              <Clock className="h-4 w-4 mr-2" />
              <span>Mon - Sat: 9:00 AM - 8:00 PM, Sun: 9:00 AM - 2:00 PM</span>
            </div>
          </div>
          
          <div className="flex space-x-4">
            <a 
              href="https://www.facebook.com/logesh.pdl?mibextid=ZbWKwL" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-swastik-lightEarth"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z"/>
              </svg>
            </a>
            <a 
              href="https://www.instagram.com/svsynclinic/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-swastik-lightEarth"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://www.youtube.com/@shrivarahamswastik4862" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-swastik-lightEarth"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
      
      {/* Main navigation */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img src={logo} alt="Shri Varaham Swastik Logo" className="h-12 w-auto" />
            <div>
              <span className="text-2xl font-serif font-bold text-swastik-blue"></span>
              <span className="block text-lg font-medium text-gray-600"></span>
            </div>
          </Link>
          
          {/* Desktop navigation */}
          <nav className="hidden lg:flex space-x-8">
            <Link 
              to="/" 
              className={`font-medium ${isActive('/') ? 'text-swastik-blue' : 'text-gray-700 hover:text-swastik-blue'} transition-colors`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`font-medium ${isActive('/about') ? 'text-swastik-blue' : 'text-gray-700 hover:text-swastik-blue'} transition-colors`}
            >
              About Us
            </Link>
            <Link 
              to="/treatments" 
              className={`font-medium ${isActive('/treatments') ? 'text-swastik-blue' : 'text-gray-700 hover:text-swastik-blue'} transition-colors`}
            >
              Treatments
            </Link>
            <Link 
              to="/gallery" 
              className={`font-medium ${isActive('/gallery') ? 'text-swastik-blue' : 'text-gray-700 hover:text-swastik-blue'} transition-colors`}
            >
              Gallery
            </Link>
            <button 
              onClick={handleFaqClick}
              className="font-medium text-gray-700 hover:text-swastik-blue transition-colors"
            >
              FAQ
            </button>
            <Link 
              to="/shop" 
              className={`font-medium ${isActive('/shop') ? 'text-swastik-blue' : 'text-gray-700 hover:text-swastik-blue'} transition-colors`}
            >
              Shop
            </Link>
            <Link 
              to="/contact" 
              className={`font-medium ${isActive('/contact') ? 'text-swastik-blue' : 'text-gray-700 hover:text-swastik-blue'} transition-colors`}
            >
              Contact
            </Link>
          </nav>
          
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              className="hidden md:flex"
              onClick={handleWhatsAppClick}
            >
              Book Appointment
            </Button>
            <button 
              className="lg:hidden text-gray-700 hover:text-swastik-blue"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <Link to="/" className="font-medium text-swastik-blue hover:bg-gray-100 px-4 py-2 rounded">Home</Link>
            <Link to="/about" className="font-medium text-gray-700 hover:bg-gray-100 px-4 py-2 rounded">About Us</Link>
            <Link to="/treatments" className="font-medium text-gray-700 hover:bg-gray-100 px-4 py-2 rounded">Treatments</Link>
            <Link to="/gallery" className="font-medium text-gray-700 hover:bg-gray-100 px-4 py-2 rounded">Gallery</Link>
            <button 
              onClick={handleFaqClick}
              className="font-medium text-gray-700 hover:bg-gray-100 px-4 py-2 rounded text-left"
            >
              FAQ
            </button>
            <Link to="/shop" className="font-medium text-gray-700 hover:bg-gray-100 px-4 py-2 rounded">Shop</Link>
            <Link to="/contact" className="font-medium text-gray-700 hover:bg-gray-100 px-4 py-2 rounded">Contact</Link>
            <Button 
              className="w-full"
              onClick={handleWhatsAppClick}
            >
              Book Appointment
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
